import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useDeployStatus, useRecommend, useSaveTarget, useProbeTarget, useResetFingerprint, useRemoveTarget, usePlan, useSetEnv, useRunState, usePublish, useDomainPlan, useDomainRunState, useDomainPublish, useDomainRevert, useReleases, useRollback, useChanges, useReleaseList } from "../../hooks/useDeploy";
import { DeployAdvice } from "./DeployAdvice";
import { usePreviewInfo } from "../../hooks/usePreviews";
import { errorMessage } from "../../hooks/useServers";

const LEVEL_ICON = { ok: "✅", warn: "🟡", bad: "❌", info: "ℹ️" };
const VERDICT = {
  listo: { icon: "✅", text: "Listo: el servidor sirve para este proyecto", cls: "text-emerald-500" },
  con_avisos: { icon: "🟡", text: "Sirve, con avisos", cls: "text-amber-500" },
  no_apto: { icon: "❌", text: "No sirve todavía", cls: "text-red-500" },
};

const RUN_ICON = { pending: "⚪", running: "⏳", ok: "✅", error: "❌" };
const RUN_STATUS = {
  running: { text: "Publicando…", cls: "text-muted-foreground" },
  done: { text: "✅ Publicado", cls: "text-emerald-500" },
  done_with_warning: { text: "🟡 Publicado, con un aviso", cls: "text-amber-500" },
  failed: { text: "❌ No se pudo publicar", cls: "text-red-500" },
};

// «Desplegar»: 1) qué servidor contratar, 2) datos del servidor y dominio, 3) revisión de solo lectura, 4) publicar (plan en palabras sencillas, variables secretas y UNA confirmación).
// La contraseña y las variables se guardan cifradas y la IA del chat nunca las ve.
function firstPendingStep(view) {
  if (!view?.configured) return 1;
  if (view.published) return 5;
  return view.probe?.verdict && view.probe.verdict !== "no_apto" ? 4 : 3;
}

export function DeployDialog({ open, onOpenChange, projectId }) {
  const { data: view, isLoading } = useDeployStatus(projectId, open);
  const recommend = useRecommend(projectId);
  const save = useSaveTarget(projectId);
  const probe = useProbeTarget(projectId);
  const reset = useResetFingerprint(projectId);
  const remove = useRemoveTarget(projectId);
  const planMutation = usePlan(projectId);
  const setEnv = useSetEnv(projectId);
  const publish = usePublish(projectId);
  const { data: runData, isLoading: runLoading } = useRunState(projectId, open && !!view?.configured);
  const run = runData?.run;
  const domainPlanMutation = useDomainPlan(projectId);
  const domainPublish = useDomainPublish(projectId);
  const domainRevert = useDomainRevert(projectId);
  const { data: domainRunData } = useDomainRunState(projectId, open && !!view?.configured);
  const domainRun = domainRunData?.run;
  const releasesMutation = useReleases(projectId);
  const rollbackMutation = useRollback(projectId);
  const quiet = open && !!view?.published && !runLoading && run?.status !== "running"; // no se compara mientras se publica (ni antes de saber si se está publicando)
  const changesQuery = useChanges(projectId, quiet); // «Al día» / «Hay cambios sin publicar»
  const releaseList = useReleaseList(projectId, quiet); // para «Deshacer el último cambio»
  const [undoTarget, setUndoTarget] = useState(null);
  const [versions, setVersions] = useState(null);
  const [rollbackTarget, setRollbackTarget] = useState(null);
  const [domainPlan, setDomainPlan] = useState(null);
  const [proxied, setProxied] = useState(false); // la nube naranja de Cloudflare es opcional: viene apagada
  const [strict, setStrict] = useState(false); // el modo SSL estricto cambia TODA la zona de Cloudflare: viene apagado
  const [republish, setRepublish] = useState(false);
  const [overwrite, setOverwrite] = useState(false);
  const [domainAgreed, setDomainAgreed] = useState(false);
  const [confirmRevert, setConfirmRevert] = useState(false);
  const [plan, setPlan] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [envValues, setEnvValues] = useState({});
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ host: "", port: "22", username: "root", password: "", domain: "", email: "" });
  const [error, setError] = useState("");
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [showSecret, setShowSecret] = useState(false); // el «ojo» de la contraseña del servidor
  const [copyDrive, setCopyDrive] = useState(false); // copiar la contraseña a Drive es opcional: viene desmarcado
  const started = useRef(false);

  // al abrir: directo al primer paso que falta (sin servidor → 1; ya publicado → 5; revisión buena → 4; si no → 3)
  useEffect(() => {
    if (!open) {
      started.current = false;
      return;
    }
    if (!view || started.current) return;
    started.current = true;
    setError("");
    setConfirmRemove(false);
    if (view.configured) {
      setForm({ host: view.host || "", port: String(view.port || 22), username: view.username || "root", password: "", domain: view.domain || "", email: view.email || "" });
      const first = firstPendingStep(view);
      setStep(first);
      if (first === 4 && !plan && !planMutation.isPending) askPlan();
      if (first === 5 && !view.domain_state?.applied && !domainPlan && !domainPlanMutation.isPending) askDomainPlan();
    } else {
      setStep(1);
      if (!view.recommendation) askRecommendation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, view]);

  const askRecommendation = async () => {
    setError("");
    try {
      await recommend.mutateAsync();
    } catch (e) {
      setError(errorMessage(e, "No se pudo revisar el proyecto en el taller."));
    }
  };
  const set = (key) => (event) => setForm((f) => ({ ...f, [key]: event.target.value }));
  const canSave = form.host.trim() && form.domain.trim() && (view?.configured || form.password);

  const saveAndProbe = async () => {
    setError("");
    try {
      await save.mutateAsync({ host: form.host.trim(), port: Number(form.port) || 22, username: form.username.trim() || "root", domain: form.domain.trim(), email: form.email.trim(), password: form.password, copy_to_drive: copyDrive });
      setForm((f) => ({ ...f, password: "" }));
      setShowSecret(false);
      setCopyDrive(false);
      setStep(3);
      await probe.mutateAsync();
    } catch (e) {
      setError(errorMessage(e, "No se pudo guardar o revisar el servidor."));
    }
  };
  const reprobe = async () => {
    setError("");
    try {
      await probe.mutateAsync();
    } catch (e) {
      setError(errorMessage(e, "No se pudo revisar el servidor."));
    }
  };
  const doReset = async () => {
    setError("");
    try {
      await reset.mutateAsync();
    } catch (e) {
      setError(errorMessage(e, "No se pudo restablecer la huella."));
    }
  };
  const doRemove = async () => {
    setError("");
    try {
      await remove.mutateAsync();
      setConfirmRemove(false);
      started.current = false;
      setForm({ host: "", port: "22", username: "root", password: "", domain: "", email: "" });
      setStep(1);
    } catch (e) {
      setError(errorMessage(e, "No se pudo quitar el servidor."));
    }
  };

  const askPlan = async () => {
    setError("");
    setAgreed(false);
    try {
      setPlan(await planMutation.mutateAsync());
    } catch (e) {
      setPlan(null);
      setError(errorMessage(e, "No se pudo preparar el plan."));
    }
  };
  const goPublish = () => {
    setStep(4);
    if (view?.configured && !plan && !planMutation.isPending) askPlan();
  };
  const saveVariable = async (name) => {
    setError("");
    try {
      await setEnv.mutateAsync({ name, value: envValues[name] || "" });
      setEnvValues((cur) => ({ ...cur, [name]: "" }));
      await askPlan(); // el plan cambia (ya no falta esa variable) y con él su hash
    } catch (e) {
      setError(errorMessage(e, "No se pudo guardar la variable."));
    }
  };
  const doPublish = async () => {
    setError("");
    setRepublish(false);
    try {
      await publish.mutateAsync(plan.hash);
    } catch (e) {
      setError(errorMessage(e, "No se pudo empezar la publicación."));
    }
  };

  const loadVersions = async () => {
    setError("");
    try {
      const data = await releasesMutation.mutateAsync();
      setVersions(data.releases);
    } catch (e) {
      setError(errorMessage(e, "No se pudieron leer las versiones del servidor."));
    }
  };
  const doRollback = async (release) => {
    setError("");
    try {
      await rollbackMutation.mutateAsync(release);
      setRollbackTarget(null);
      setVersions(null);
    } catch (e) {
      setRollbackTarget(null);
      setError(errorMessage(e, "No se pudo volver a esa versión."));
    }
  };
  const askDomainPlan = async (opts = {}) => {
    const next = { proxied, strict, overwrite, ...opts };
    setError("");
    setDomainAgreed(false);
    try {
      setDomainPlan(await domainPlanMutation.mutateAsync(next));
    } catch (e) {
      setDomainPlan(null);
      setError(errorMessage(e, "No se pudo preparar el plan del dominio."));
    }
  };
  const goDomain = () => {
    setStep(5);
    if (view?.configured && view?.published && !domainPlan && !domainPlanMutation.isPending) askDomainPlan();
  };
  const changeProxied = (value) => {
    setProxied(value);
    if (!value) setStrict(false);
    askDomainPlan(value ? { proxied: true } : { proxied: false, strict: false });
  };
  const changeStrict = (value) => {
    setStrict(value);
    askDomainPlan({ strict: value });
  };
  const changeOverwrite = (value) => {
    setOverwrite(value);
    askDomainPlan({ overwrite: value });
  };
  const doDomainPublish = async () => {
    setError("");
    try {
      await domainPublish.mutateAsync({ planHash: domainPlan.hash, proxied, strict, overwrite });
    } catch (e) {
      setError(errorMessage(e, "No se pudo empezar la configuración del dominio."));
    }
  };
  const doRevert = async () => {
    setError("");
    try {
      await domainRevert.mutateAsync();
      setConfirmRevert(false);
      setDomainPlan(null);
    } catch (e) {
      setError(errorMessage(e, "No se pudo revertir el dominio."));
    }
  };

  // «¿Qué hago?» (Fase C): cada botón de una sugerencia es un paso que esta ventana ya tiene (con sus propias confirmaciones); la sugerencia no ejecuta nada sola.
  const publishChanges = () => { setStep(4); setRepublish(true); askPlan(); };
  const doUndo = async () => {
    const release = undoTarget;
    setUndoTarget(null);
    setStep(4);
    await doRollback(release);
  };
  const live = changesQuery.data;
  const liveState = { al_dia: { text: "🟢 Al día: lo publicado es igual a lo del taller", cls: "text-emerald-500" }, con_cambios: { text: "🟡 Hay cambios sin publicar", cls: "text-amber-500" } }[live?.state];
  // «el último cambio» = la versión más nueva que sea anterior a la activa (y que se publicó bien)
  const previous = (releaseList.data?.releases || []).filter((r) => r.ok !== false && !r.active && r.id < (releaseList.data?.current || "")).sort((a, b) => b.id.localeCompare(a.id))[0] || null;
  const adviceHandlers = {
    reprobe: () => { setStep(3); reprobe(); },
    reset_fingerprint: () => { setStep(3); doReset(); },
    edit_data: () => setStep(2),
    republish: publishChanges,
    show_versions: () => { setStep(4); loadVersions(); },
    domain_replan: () => { setStep(5); askDomainPlan(); },
    no_proxy: () => { setStep(5); changeProxied(false); },
  };
  const domainOptions = { proxied, strict, overwrite };

  const rec = view?.recommendation;
  const result = view?.probe;
  const busy = save.isPending || probe.isPending;
  const publishing = run?.status === "running";
  const justPublished = !!run && run.kind !== "rollback" && (run.status === "done" || run.status === "done_with_warning") && !republish; // ya publicado: no se ofrece publicar otra vez por error (doble clic)
  const configuringDomain = domainRun?.status === "running";

  // Lista de pasos: ✅ hecho, ▶ el que estás viendo, ⚪ pendiente, 🔒 todavía no se puede. Los pasos 1 y 2 se cierran al terminar; el 3, 4 y 5 siguen abiertos
  // porque ahí viven «revisar de nuevo», las versiones publicadas y «revertir dominio» (sus botones de hacer-otra-vez ya no se ofrecen).
  const done = { 1: !!view?.configured, 2: !!view?.configured, 3: !!view?.configured && !!result?.verdict && result.verdict !== "no_apto", 4: !!view?.published, 5: !!view?.domain_state?.applied };
  const locked = { 1: !!view?.configured, 2: !!view?.published, 3: !view?.configured, 4: !view?.configured, 5: !view?.published };
  const summary = {
    1: done[1] ? "Servidor elegido" : "Te digo qué servidor contratar",
    2: done[2] ? `${view?.host || ""} → ${view?.domain || ""}` : "IP, usuario, contraseña y dominio",
    3: done[3] ? `${VERDICT[result.verdict]?.text || "Revisado"}` : "Compruebo que el servidor sirva (solo lectura)",
    4: done[4] ? "Publicado" : "Con tu confirmación",
    5: done[5] ? `${view?.domain_state?.domain || view?.domain} con candado` : view?.published ? "Pendiente: conectar el dominio" : "Se hace después de publicar",
  };
  const titles = ["Qué contratar", "Datos del servidor", "Revisión", "Publicar", "Dominio y candado"];
  const liveUrl = view?.published ? (view.domain_state?.applied ? `https://${view.domain_state.domain || view.domain}/` : run?.url || `http://${view.host}/`) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="deploy-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>🚀 Desplegar en producción</DialogTitle>
          <DialogDescription>
            Te digo qué servidor contratar, guardo sus datos (cifrados), reviso que sirva y, con tu confirmación, publico. Nada se publica sin que tú lo confirmes.
          </DialogDescription>
        </DialogHeader>

        {liveUrl && (
          <div data-testid="deploy-live" className="rounded-lg border border-emerald-500/40 p-3 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm">
                ✅ En producción: <span data-testid="deploy-live-url" className="font-mono break-all">{liveUrl}</span>
                {!view.domain_state?.applied && <span className="text-xs text-muted-foreground"> (por ahora sin dominio ni candado: falta el paso 5)</span>}
              </p>
              <a data-testid="deploy-live-open" href={liveUrl} target="_blank" rel="noopener noreferrer" className="text-sm underline">Abrir sitio</a>
            </div>
            {live?.release && (
              <p data-testid="deploy-live-version" className="text-xs text-muted-foreground">
                Versión activa: {live.release_label}
                {liveState && <span data-testid="deploy-live-state" className={`ml-2 ${liveState.cls}`}>{liveState.text}</span>}
              </p>
            )}
            {live?.state === "desconocido" && live.message && <p data-testid="deploy-live-unknown" className="text-xs text-muted-foreground">{live.message}</p>}
            <div className="flex flex-wrap gap-2">
              <Button data-testid="deploy-publish-changes" size="sm" variant={live?.state === "con_cambios" ? "default" : "outline"} onClick={publishChanges} disabled={publishing || planMutation.isPending}>🚀 Publicar cambios</Button>
              <Button data-testid="deploy-undo" size="sm" variant="outline" onClick={() => setUndoTarget(previous.id)} disabled={!previous || publishing || rollbackMutation.isPending} title={previous ? "" : "Todavía no hay una versión anterior"}>↩️ Deshacer el último cambio</Button>
            </div>
            {undoTarget && previous && (
              <div data-testid="deploy-undo-confirm" className="rounded-md border border-amber-500/40 p-2 space-y-2">
                <p className="text-xs">¿Volver a la versión del {previous.at}? Se activa y se comprueba que responde; si no responde, vuelve sola a la actual. Los cambios del taller no se pierden: podrás publicarlos de nuevo.</p>
                <div className="flex gap-2">
                  <Button data-testid="deploy-undo-yes" size="sm" variant="outline" onClick={doUndo}>Sí, deshacer</Button>
                  <Button data-testid="deploy-undo-no" size="sm" variant="outline" onClick={() => setUndoTarget(null)}>No</Button>
                </div>
              </div>
            )}
          </div>
        )}

        <ol data-testid="deploy-steps" className="grid gap-1 text-xs">
          {titles.map((title, i) => {
            const n = i + 1;
            const state = locked[n] ? (done[n] ? "done" : "locked") : step === n ? "current" : done[n] ? "done" : "pending";
            const icon = locked[n] && !done[n] ? "🔒" : done[n] ? "✅" : step === n ? "▶" : "⚪";
            return (
              <li key={title}>
                <button type="button" data-testid={`deploy-step-${n}`} data-state={state} disabled={locked[n]} onClick={() => (n === 4 ? goPublish() : n === 5 ? goDomain() : setStep(n))}
                  className={`w-full rounded-md border px-3 py-1.5 text-left flex gap-2 items-baseline disabled:opacity-60 disabled:cursor-not-allowed ${step === n ? "border-emerald-500 text-foreground" : "border-border text-muted-foreground"}`}>
                  <span>{icon}</span>
                  <span className="font-medium">{n} · {title}</span>
                  <span className="text-muted-foreground truncate">— {summary[n]}</span>
                </button>
              </li>
            );
          })}
        </ol>

        {isLoading && <p className="text-sm text-muted-foreground">Cargando…</p>}
        {error && (
          <p data-testid="deploy-error" className="text-sm text-amber-500">
            {error}
          </p>
        )}

        <div className="max-h-[55vh] overflow-y-auto pr-1 space-y-3">
          {step === 1 && (
            <div data-testid="deploy-step1" className="space-y-3">
              <button type="button" data-testid="deploy-skip-1" className="text-xs underline" onClick={() => setStep(2)}>¿Ya tienes servidor? Ir directo a escribir sus datos →</button>
              {recommend.isPending && <p data-testid="deploy-recommending" className="text-sm text-muted-foreground">Revisando lo que se construyó en el taller…</p>}
              {rec && (
                <>
                  <div className="rounded-lg border border-border p-3 space-y-1">
                    <p className="text-sm font-medium">Lo que construiste</p>
                    <p data-testid="deploy-kind" className="text-sm">{rec.detected.kind_label}{rec.detected.db_label ? ` · usa ${rec.detected.db_label}` : ""}</p>
                  </div>
                  <div className="rounded-lg border border-border p-3 space-y-2">
                    <p className="text-sm font-medium">Servidor recomendado</p>
                    <div data-testid="deploy-specs" className="grid grid-cols-3 gap-2 text-center">
                      <div className="rounded-md bg-muted/40 p-2"><p className="text-lg font-semibold">{rec.specs.vcpu}</p><p className="text-xs text-muted-foreground">procesador(es) vCPU</p></div>
                      <div className="rounded-md bg-muted/40 p-2"><p className="text-lg font-semibold">{rec.specs.ram_gb} GB</p><p className="text-xs text-muted-foreground">memoria RAM</p></div>
                      <div className="rounded-md bg-muted/40 p-2"><p className="text-lg font-semibold">{rec.specs.disk_gb} GB</p><p className="text-xs text-muted-foreground">disco</p></div>
                    </div>
                    <ul className="list-disc pl-5 text-xs space-y-0.5">{rec.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
                  </div>
                  <div className="rounded-lg border border-border p-3 space-y-1">
                    <p className="text-sm font-medium">Al contratarlo pide</p>
                    <ul data-testid="deploy-requirements" className="list-disc pl-5 text-xs space-y-0.5">{rec.requirements.map((r) => <li key={r}>{r}</li>)}</ul>
                    <p className="text-sm font-medium pt-1">Dónde</p>
                    <ul className="list-disc pl-5 text-xs space-y-0.5">{rec.providers.map((p) => <li key={p.name}><strong>{p.name}:</strong> {p.note}</li>)}</ul>
                  </div>
                </>
              )}
              <div className="flex gap-2 justify-end">
                <Button data-testid="deploy-recheck" variant="outline" size="sm" onClick={askRecommendation} disabled={recommend.isPending}>Volver a revisar el proyecto</Button>
                <Button data-testid="deploy-next-1" size="sm" onClick={() => setStep(2)}>Ya tengo el servidor →</Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="deploy-step2" className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2 space-y-1">
                  <Label htmlFor="deploy-host">IP del servidor</Label>
                  <Input id="deploy-host" data-testid="deploy-host" value={form.host} onChange={set("host")} placeholder="Ej. 203.0.113.10" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="deploy-port">Puerto SSH</Label>
                  <Input id="deploy-port" data-testid="deploy-port" value={form.port} onChange={set("port")} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="deploy-user">Usuario</Label>
                  <Input id="deploy-user" data-testid="deploy-user" value={form.username} onChange={set("username")} />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label htmlFor="deploy-pass">{view?.configured ? "Contraseña (déjala vacía para conservar la guardada)" : "Contraseña"}</Label>
                  <div className="relative">
                    <Input id="deploy-pass" data-testid="deploy-password" type={showSecret ? "text" : "password"} autoComplete="new-password" className="pr-20" value={form.password} onChange={set("password")} />
                    <button type="button" data-testid="deploy-password-toggle" aria-label={showSecret ? "Ocultar contraseña" : "Ver contraseña"} className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground" onClick={() => setShowSecret((v) => !v)}>
                      {showSecret ? "🙈 Ocultar" : "👁 Ver"}
                    </button>
                  </div>
                </div>
                <div className="col-span-2 space-y-1">
                  <Label htmlFor="deploy-domain">Dominio donde vivirá</Label>
                  <Input id="deploy-domain" data-testid="deploy-domain" value={form.domain} onChange={set("domain")} placeholder="Ej. mipagina.com.mx" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="deploy-email">Correo (opcional)</Label>
                  <Input id="deploy-email" data-testid="deploy-email" value={form.email} onChange={set("email")} placeholder="Avisos del SSL" />
                </div>
              </div>
              <p data-testid="deploy-cloudflare" className={`text-xs ${view?.cloudflare_token ? "text-emerald-500" : "text-amber-500"}`}>
                {view?.cloudflare_token ? "✅ El token de Cloudflare está guardado en «Claves»." : "🟡 Falta guardar el token de Cloudflare en «Claves» (se usará para el dominio y el SSL). Puedes seguir y ponerlo después."}
              </p>
              <p className="text-xs text-muted-foreground">La contraseña se guarda cifrada y la IA nunca la ve. Cámbiala en tu proveedor cuando termines.</p>
              <label className="flex items-start gap-2 text-xs">
                <input data-testid="deploy-copy-drive" type="checkbox" checked={copyDrive} onChange={(e) => setCopyDrive(e.target.checked)} className="mt-0.5" />
                <span>Guardar también una copia de esta contraseña en mi carpeta de Drive (09_Credenciales_y_Accesos.md). Solo si aún no usas un gestor de contraseñas.</span>
              </label>
              <div className="flex gap-2 justify-end">
                <Button data-testid="deploy-back-2" variant="outline" size="sm" onClick={() => setStep(1)}>← Atrás</Button>
                <Button data-testid="deploy-save" size="sm" onClick={saveAndProbe} disabled={busy || !canSave}>
                  {busy ? "Guardando y revisando…" : "Guardar y revisar el servidor"}
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="deploy-step3" className="space-y-3">
              {!view?.configured && <p className="text-sm text-muted-foreground">Todavía no has guardado los datos del servidor (paso 2).</p>}
              {view?.configured && (
                <div className="rounded-lg border border-border p-3 space-y-1">
                  <p className="text-xs font-mono text-muted-foreground" data-testid="deploy-target">{view.username}@{view.host}:{view.port} → {view.domain}</p>
                  {view.fingerprint && <p className="text-xs text-muted-foreground break-all" data-testid="deploy-fingerprint">Huella del servidor (la recuerdo desde la primera conexión): {view.fingerprint}</p>}
                </div>
              )}
              {probe.isPending && <p data-testid="deploy-probing" className="text-sm text-muted-foreground">Revisando el servidor (solo lectura, no se cambia nada)…</p>}
              {result && !probe.isPending && (
                <div data-testid="deploy-result" className="space-y-2">
                  <p data-testid="deploy-verdict" className={`text-sm font-medium ${VERDICT[result.verdict]?.cls || ""}`}>
                    {VERDICT[result.verdict]?.icon} {VERDICT[result.verdict]?.text || "Sin veredicto"} <span className="text-xs text-muted-foreground font-normal">({result.at})</span>
                  </p>
                  {result.message && <p data-testid="deploy-probe-message" className="text-xs text-red-500">{result.message}</p>}
                  {result.verdict === "no_apto" && <DeployAdvice projectId={projectId} kind="probe" name="probe" resetKey={result.at} handlers={adviceHandlers} />}
                  <ul className="space-y-1">
                    {result.checks.map((c) => (
                      <li key={c.id} data-testid={`deploy-check-${c.id}`} data-level={c.level} className="text-xs flex gap-2">
                        <span>{LEVEL_ICON[c.level]}</span>
                        <span><strong>{c.label}:</strong> {c.detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {view?.configured && (
                <div className="flex flex-wrap gap-2 justify-end">
                  {/huella/i.test(result?.message || "") && (
                    <Button data-testid="deploy-reset-fp" variant="outline" size="sm" onClick={doReset} disabled={reset.isPending}>Restablecer huella</Button>
                  )}
                  {!view.published && <Button data-testid="deploy-edit" variant="outline" size="sm" onClick={() => setStep(2)}>Editar datos</Button>}
                  <Button data-testid="deploy-reprobe" size="sm" onClick={reprobe} disabled={probe.isPending}>{probe.isPending ? "Revisando…" : result ? "Volver a revisar" : "Revisar el servidor"}</Button>
                </div>
              )}
              {view?.configured && !confirmRemove && (
                <button type="button" data-testid="deploy-remove" className="text-xs text-red-500 underline" onClick={() => setConfirmRemove(true)}>Quitar este servidor del proyecto</button>
              )}
              {confirmRemove && (
                <div data-testid="deploy-remove-confirm" className="rounded-lg border border-red-500/40 p-3 space-y-2">
                  <p className="text-xs">Se borran los datos guardados de este servidor (incluida la contraseña cifrada). El servidor en sí no se toca.</p>
                  <div className="flex gap-2">
                    <Button data-testid="deploy-remove-yes" size="sm" variant="outline" onClick={doRemove} disabled={remove.isPending}>Sí, quitarlo</Button>
                    <Button data-testid="deploy-remove-no" size="sm" variant="outline" onClick={() => setConfirmRemove(false)}>No</Button>
                  </div>
                </div>
              )}
              {view?.configured && result && result.verdict !== "no_apto" && (
                <div className="flex justify-end">
                  <Button data-testid="deploy-next-3" size="sm" onClick={goPublish}>Siguiente: publicar →</Button>
                </div>
              )}
              <p className="text-xs text-muted-foreground">Cuando el servidor sirva, sigue al paso 4 para publicar. Nada se publica sin tu confirmación.</p>
            </div>
          )}

          {step === 4 && (
            <div data-testid="deploy-step4" className="space-y-3">
              {!view?.configured && <p className="text-sm text-muted-foreground">Primero guarda los datos del servidor (paso 2) y revísalo (paso 3).</p>}
              {planMutation.isPending && <p data-testid="deploy-planning" className="text-sm text-muted-foreground">Preparando el plan (reviso el proyecto en el taller, solo lectura)…</p>}

              {plan && !planMutation.isPending && (
                <div data-testid="deploy-plan" className="space-y-3">
                  <p className="text-xs text-muted-foreground" data-testid="deploy-plan-source">
                    {plan.source === "archivo" ? "Cómo publicarlo: lo dice el despliegue.json del proyecto." : plan.source === "automatico" ? "Cómo publicarlo: lo deduje yo (es una página sencilla)." : ""}
                  </p>
                  {plan.blocked?.length > 0 && (
                    <div data-testid="deploy-blocked" className="rounded-lg border border-amber-500/40 p-3 space-y-1">
                      <p className="text-sm font-medium text-amber-500">Todavía no se puede publicar</p>
                      <ul className="list-disc pl-5 text-xs space-y-0.5">{plan.blocked.map((b) => <li key={b}>{b}</li>)}</ul>
                    </div>
                  )}
                  {plan.steps?.length > 0 && (
                    <div className="rounded-lg border border-border p-3 space-y-2">
                      <p className="text-sm font-medium">Esto es lo que voy a hacer, en orden</p>
                      <ol data-testid="deploy-plan-steps" className="list-decimal pl-5 text-xs space-y-1.5">
                        {plan.steps.map((s) => (
                          <li key={s.id} data-testid={`deploy-plan-step-${s.id}`}><strong>{s.title}.</strong> {s.detail}</li>
                        ))}
                      </ol>
                    </div>
                  )}
                  {plan.warnings?.length > 0 && (
                    <ul data-testid="deploy-warnings" className="list-disc pl-5 text-xs space-y-0.5 text-amber-500">{plan.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
                  )}
                  {plan.env?.length > 0 && (
                    <div data-testid="deploy-env" className="rounded-lg border border-border p-3 space-y-2">
                      <p className="text-sm font-medium">Variables secretas de la aplicación</p>
                      <p className="text-xs text-muted-foreground">Se guardan cifradas y se escriben en el servidor en un archivo que solo lee la aplicación. La IA nunca las ve.</p>
                      {plan.env.map((v) => (
                        <div key={v.name} data-testid={`deploy-env-${v.name}`} className="flex items-center gap-2">
                          <span className="text-xs font-mono w-44 shrink-0">{v.name}</span>
                          {v.saved ? (
                            <span data-testid={`deploy-env-saved-${v.name}`} className="text-xs text-emerald-500">✅ guardada ({v.hint})</span>
                          ) : (
                            <>
                              <Input data-testid={`deploy-env-input-${v.name}`} type="password" autoComplete="new-password" className="h-8" value={envValues[v.name] || ""} onChange={(e) => setEnvValues((cur) => ({ ...cur, [v.name]: e.target.value }))} placeholder="Valor" />
                              <Button data-testid={`deploy-env-save-${v.name}`} size="sm" variant="outline" disabled={!(envValues[v.name] || "").trim() || setEnv.isPending} onClick={() => saveVariable(v.name)}>Guardar</Button>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {!plan.blocked?.length && plan.hash && !publishing && !justPublished && (
                    <div className="rounded-lg border border-emerald-500/40 p-3 space-y-2">
                      <label className="flex items-start gap-2 text-xs">
                        <input data-testid="deploy-agree" type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5" />
                        <span>Entiendo lo que hará cada paso y confirmo este plan completo: se instalará y publicará en <strong>{view?.host}</strong> para <strong>{view?.domain}</strong>.</span>
                      </label>
                      <Button data-testid="deploy-publish" size="sm" onClick={doPublish} disabled={!agreed || publish.isPending}>{publish.isPending ? "Empezando…" : "🚀 Publicar ahora"}</Button>
                    </div>
                  )}
                </div>
              )}

              {run && (
                <div data-testid="deploy-run" className="rounded-lg border border-border p-3 space-y-2">
                  <p data-testid="deploy-run-status" className={`text-sm font-medium ${RUN_STATUS[run.status]?.cls || ""}`}>{run.kind === "rollback" && run.status === "running" ? "Volviendo a una versión anterior…" : RUN_STATUS[run.status]?.text || run.status}</p>
                  <ul className="space-y-1">
                    {run.steps.map((s) => (
                      <li key={s.id} data-testid={`deploy-run-step-${s.id}`} data-status={s.status} className="text-xs flex gap-2">
                        <span>{RUN_ICON[s.status]}</span>
                        <span><strong>{s.title}</strong>{s.detail ? ` — ${s.detail}` : ""}</span>
                      </li>
                    ))}
                  </ul>
                  {run.message && run.status !== "done" && <p data-testid="deploy-run-message" className={`text-xs ${run.status === "failed" ? "text-red-500" : "text-amber-500"}`}>{run.message}</p>}
                  {(run.status === "failed" || run.status === "done_with_warning") && <DeployAdvice projectId={projectId} kind="publish" name="publish" resetKey={run.id} handlers={adviceHandlers} />}
                  {run.url && (run.status === "done" || run.status === "done_with_warning") && (
                    <p className="text-xs">
                      Abre tu sitio: <a data-testid="deploy-run-url" className="underline" href={run.url} target="_blank" rel="noopener noreferrer">{run.url}</a>{view?.domain_state?.applied ? "" : " (por ahora sin candado; en el paso 5 se conecta el dominio y se activa el SSL)."}
                    </p>
                  )}
                  {run.status === "done" && run.kind !== "rollback" && view?.published && !view?.domain_state?.applied && (
                    <div className="flex justify-end">
                      <Button data-testid="deploy-next-4" size="sm" onClick={goDomain}>Siguiente: dominio y candado →</Button>
                    </div>
                  )}
                </div>
              )}

              {view?.configured && view?.published && (
                <div data-testid="deploy-versions" className="rounded-lg border border-border p-3 space-y-2">
                  <p className="text-sm font-medium">Versiones publicadas</p>
                  <p className="text-xs text-muted-foreground">Cada publicación queda guardada (las últimas 3). Si la nueva falla, puedes volver a una anterior: solo cambia el código; la configuración y las variables se quedan igual.</p>
                  {!versions && (
                    <Button data-testid="deploy-versions-load" size="sm" variant="outline" onClick={loadVersions} disabled={releasesMutation.isPending || publishing}>{releasesMutation.isPending ? "Leyendo el servidor…" : "Ver versiones publicadas"}</Button>
                  )}
                  {versions && versions.length === 0 && <p className="text-xs text-muted-foreground">No hay versiones guardadas en el servidor.</p>}
                  {versions && versions.length > 0 && (
                    <ul className="space-y-1.5">
                      {versions.map((v) => (
                        <li key={v.id} data-testid={`deploy-version-${v.id}`} className="text-xs flex flex-wrap items-center gap-2">
                          <span className="font-mono">{v.id}</span>
                          <span className="text-muted-foreground">{v.at}</span>
                          {v.active ? (
                            <span data-testid={`deploy-version-active-${v.id}`} className="text-emerald-500">✅ activa</span>
                          ) : rollbackTarget === v.id ? (
                            <span data-testid="deploy-rollback-confirm" className="flex items-center gap-2">
                              <span>¿Volver a esta versión? Se activa y se comprueba que responde; si no responde, vuelve sola a la actual.</span>
                              <Button data-testid="deploy-rollback-yes" size="sm" variant="outline" onClick={() => doRollback(v.id)} disabled={rollbackMutation.isPending}>Sí, volver</Button>
                              <Button data-testid="deploy-rollback-no" size="sm" variant="outline" onClick={() => setRollbackTarget(null)}>No</Button>
                            </span>
                          ) : (
                            <Button data-testid={`deploy-rollback-${v.id}`} size="sm" variant="outline" onClick={() => setRollbackTarget(v.id)} disabled={publishing}>Volver a esta versión</Button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {view?.configured && (
                <div className="flex justify-end gap-2">
                  <Button data-testid="deploy-replan" variant="outline" size="sm" onClick={publishChanges} disabled={planMutation.isPending || publishing}>🚀 Publicar cambios (revisar el plan de nuevo)</Button>
                </div>
              )}
            </div>
          )}

          {step === 5 && (
            <div data-testid="deploy-step5" className="space-y-3">
              {!view?.configured && <p className="text-sm text-muted-foreground">Primero guarda los datos del servidor (paso 2).</p>}
              {view?.configured && !view?.published && <p data-testid="deploy-domain-needs-publish" className="text-sm text-amber-500">Primero publica el proyecto (paso 4): el dominio se conecta a algo que ya funciona.</p>}
              {domainPlanMutation.isPending && <p data-testid="deploy-domain-planning" className="text-sm text-muted-foreground">Revisando Cloudflare (solo lectura, no se cambia nada)…</p>}

              {view?.domain_state?.applied && (
                <div data-testid="deploy-domain-applied" className="rounded-lg border border-emerald-500/40 p-3 space-y-2">
                  <p className="text-sm font-medium text-emerald-500">✅ {view.domain_state.domain} está conectado{view.domain_state.proxied ? " (con la nube naranja)" : ""} y con candado.</p>
                  {!confirmRevert ? (
                    <button type="button" data-testid="deploy-revert" className="text-xs text-red-500 underline" onClick={() => setConfirmRevert(true)}>Revertir dominio</button>
                  ) : (
                    <div data-testid="deploy-revert-confirm" className="space-y-2">
                      <p className="text-xs">Los registros DNS y el modo SSL de Cloudflare volverán a como estaban antes. El sitio seguirá publicado por HTTP en la IP del servidor.</p>
                      <div className="flex gap-2">
                        <Button data-testid="deploy-revert-yes" size="sm" variant="outline" onClick={doRevert} disabled={domainRevert.isPending}>Sí, revertir</Button>
                        <Button data-testid="deploy-revert-no" size="sm" variant="outline" onClick={() => setConfirmRevert(false)}>No</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {domainPlan && !domainPlanMutation.isPending && (
                <div data-testid="deploy-domain-plan" className="space-y-3">
                  {domainPlan.blocked?.length > 0 && (
                    <div data-testid="deploy-domain-blocked" className="rounded-lg border border-amber-500/40 p-3 space-y-1">
                      <p className="text-sm font-medium text-amber-500">Todavía no se puede configurar</p>
                      <ul className="list-disc pl-5 text-xs space-y-0.5">{domainPlan.blocked.map((b) => <li key={b}>{b}</li>)}</ul>
                      <DeployAdvice projectId={projectId} kind="domain" name="domain-plan" options={domainOptions} resetKey={JSON.stringify(domainPlan.blocked)} handlers={adviceHandlers} />
                    </div>
                  )}
                  {domainPlan.records?.length > 0 && (
                    <div className="rounded-lg border border-border p-3 space-y-1">
                      <p className="text-sm font-medium">Registros DNS en Cloudflare{domainPlan.zone ? ` (zona ${domainPlan.zone})` : ""}</p>
                      <table data-testid="deploy-domain-records" className="w-full text-xs">
                        <thead><tr className="text-left text-muted-foreground"><th>Nombre</th><th>Hoy</th><th>Después</th></tr></thead>
                        <tbody>
                          {domainPlan.records.map((r) => (
                            <tr key={r.name} data-testid={`deploy-domain-record-${r.name}`}>
                              <td className="font-mono">{r.name}</td>
                              <td>{r.before ? `${r.before.content}${r.before.proxied ? " (nube naranja)" : ""}` : "no existe"}</td>
                              <td>{r.after.content}{r.after.proxied ? " (nube naranja)" : ""}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {domainPlan.steps?.length > 0 && (
                    <div className="rounded-lg border border-border p-3 space-y-2">
                      <p className="text-sm font-medium">Esto es lo que voy a hacer, en orden</p>
                      <ol data-testid="deploy-domain-steps" className="list-decimal pl-5 text-xs space-y-1.5">
                        {domainPlan.steps.map((s) => <li key={s.id} data-testid={`deploy-domain-step-${s.id}`}><strong>{s.title}.</strong> {s.detail}</li>)}
                      </ol>
                    </div>
                  )}
                  {domainPlan.warnings?.length > 0 && <ul data-testid="deploy-domain-warnings" className="list-disc pl-5 text-xs space-y-0.5 text-amber-500">{domainPlan.warnings.map((w) => <li key={w}>{w}</li>)}</ul>}
                  {view?.published && (
                    <div className="rounded-lg border border-border p-3 space-y-2">
                      <label className="flex items-start gap-2 text-xs">
                        <input data-testid="deploy-proxied" type="checkbox" checked={proxied} onChange={(e) => changeProxied(e.target.checked)} className="mt-0.5" disabled={configuringDomain} />
                        <span>Proteger con Cloudflare (nube naranja): oculta la IP del servidor. Es opcional: NO hace falta para tener el candado.</span>
                      </label>
                      {proxied && (
                        <div className="space-y-1">
                          <label className="flex items-start gap-2 text-xs">
                            <input data-testid="deploy-strict" type="checkbox" checked={strict} onChange={(e) => changeStrict(e.target.checked)} className="mt-0.5" disabled={configuringDomain || !!domainPlan.options?.strict_blocked} />
                            <span>Modo SSL estricto de Cloudflare.</span>
                          </label>
                          <p data-testid="deploy-strict-warning" className="text-xs text-red-500">
                            ⚠️ Cambia el modo SSL de TODA tu zona de Cloudflare, no solo de este sitio: si otro sitio de tu zona no tiene un certificado válido, dejará de abrir (error 526).{domainPlan.options?.strict_blocked ? ` Bloqueado: ${domainPlan.options.strict_blocked}` : " Antes de aplicarlo comprobé que los demás sitios de la zona lo soportan."}
                          </p>
                        </div>
                      )}
                      {domainPlan.needs_overwrite && (
                        <label className="flex items-start gap-2 text-xs text-amber-500">
                          <input data-testid="deploy-overwrite" type="checkbox" checked={overwrite} onChange={(e) => changeOverwrite(e.target.checked)} className="mt-0.5" disabled={configuringDomain} />
                          <span>Entiendo que se cambiarán registros que hoy apuntan a otro lado (si ese sitio está en uso, dejará de verse).</span>
                        </label>
                      )}
                    </div>
                  )}
                  {!domainPlan.blocked?.length && domainPlan.hash && !configuringDomain && !view?.domain_state?.applied && (
                    <div className="rounded-lg border border-emerald-500/40 p-3 space-y-2">
                      <label className="flex items-start gap-2 text-xs">
                        <input data-testid="deploy-domain-agree" type="checkbox" checked={domainAgreed} onChange={(e) => setDomainAgreed(e.target.checked)} className="mt-0.5" />
                        <span>Entiendo lo que hará cada paso y confirmo este plan completo para <strong>{view?.domain}</strong>.</span>
                      </label>
                      <Button data-testid="deploy-domain-publish" size="sm" onClick={doDomainPublish} disabled={!domainAgreed || domainPublish.isPending}>{domainPublish.isPending ? "Empezando…" : "🔒 Configurar dominio y candado"}</Button>
                    </div>
                  )}
                </div>
              )}

              {domainRun && (
                <div data-testid="deploy-domain-run" className="rounded-lg border border-border p-3 space-y-2">
                  <p data-testid="deploy-domain-run-status" className={`text-sm font-medium ${{ running: "text-muted-foreground", done: "text-emerald-500", done_with_warning: "text-amber-500", failed: "text-red-500" }[domainRun.status] || ""}`}>
                    {{ running: "Configurando…", done: "✅ Dominio y candado listos", done_with_warning: "🟡 Configurado, con un aviso", failed: "❌ No se pudo configurar" }[domainRun.status] || domainRun.status}
                  </p>
                  <ul className="space-y-1">
                    {domainRun.steps.map((s) => (
                      <li key={s.id} data-testid={`deploy-domain-run-step-${s.id}`} data-status={s.status} className="text-xs flex gap-2">
                        <span>{RUN_ICON[s.status]}</span>
                        <span><strong>{s.title}</strong>{s.detail ? ` — ${s.detail}` : ""}</span>
                      </li>
                    ))}
                  </ul>
                  {domainRun.message && domainRun.status !== "done" && <p data-testid="deploy-domain-run-message" className={`text-xs ${domainRun.status === "failed" ? "text-red-500" : "text-amber-500"}`}>{domainRun.message}</p>}
                  {(domainRun.status === "failed" || domainRun.status === "done_with_warning") && <DeployAdvice projectId={projectId} kind="domain" name="domain-run" options={domainOptions} resetKey={domainRun.id} handlers={adviceHandlers} />}
                  {domainRun.url && (domainRun.status === "done" || domainRun.status === "done_with_warning") && (
                    <p className="text-xs">Abre tu sitio: <a data-testid="deploy-domain-run-url" className="underline" href={domainRun.url} target="_blank" rel="noopener noreferrer">{domainRun.url}</a></p>
                  )}
                </div>
              )}

              {view?.configured && view?.published && (
                <div className="flex justify-end">
                  <Button data-testid="deploy-domain-replan" variant="outline" size="sm" onClick={() => askDomainPlan()} disabled={domainPlanMutation.isPending || configuringDomain}>Volver a revisar el plan</Button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button data-testid="deploy-close" variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cerrar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Escucha «abrir desplegar» (lo manda el botón del encabezado o el del mensaje con la captura) y muestra la ventana del proyecto actual.
export function DeployHost({ projectId }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-deploy", handler);
    return () => window.removeEventListener("open-deploy", handler);
  }, []);
  return <DeployDialog open={open} onOpenChange={setOpen} projectId={projectId} />;
}

export function openDeploy() {
  window.dispatchEvent(new CustomEvent("open-deploy"));
}

// Botón «Desplegar» del encabezado del chat: solo en proyectos del taller.
export function DeployButton({ projectId }) {
  const { data: info } = usePreviewInfo(projectId);
  if (!info?.is_workshop) return null;
  return (
    <Button data-testid="deploy-button" size="sm" variant="outline" onClick={openDeploy}>
      🚀 Desplegar
    </Button>
  );
}
